import api from "@/utils/api";
import { ApiResponse } from "@/constants/api";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import logger from "@/utils/logger";

// 리뷰 이미지 정보 인터페이스
export interface ReviewImageInfo {
    altKey: string;
    imageUrl: string;
    createdAt: string;
}

// API에서 받는 리뷰 정보 인터페이스
export interface ReviewInfo {
    altKey: string;
    userAltKey: string;
    productAltKey: string;
    content: string;
    rating: number;
    reviewImageInfoList: ReviewImageInfo[];
    createdAt: string;
}

// 리뷰 목록 페이지네이션 정보
export interface PaginationInfo {
    totalRecords: number;
    currentPage: number;
    totalPages: number;
    nextPage: number | null;
    prevPage: number | null;
}

// 리뷰 목록 응답 데이터
export interface ReviewListResponse {
    reviewInfoList: ReviewInfo[];
    pagination: PaginationInfo;
}

// 화면에 표시할 리뷰 정보 인터페이스
export interface DisplayReview {
    altKey: string;
    rating: string;
    content: string;
    date: string;
    images: string[];
}

/**
 * 상품의 리뷰 목록을 가져옵니다.
 * @param productAltKey 상품의 고유 키
 * @param page 페이지 번호 (0부터 시작)
 * @param limit 한 번에 가져올 리뷰 수
 * @returns 리뷰 목록 및 페이지네이션 정보
 */
export const fetchProductReviews = async (
    productAltKey: string,
    page: number = 0,
    limit: number = 3
): Promise<{ reviews: DisplayReview[]; totalCount: number; hasMore: boolean } | null> => {
    try {
        const response = await api.get<ApiResponse<ReviewListResponse>>(
            `/api/v1/reviews?productAltKey=${productAltKey}&page=${page}&size=${limit}`
        );

        if (response.data.success && response.data.data) {
            const { reviewInfoList, pagination } = response.data.data;

            // 디버깅을 위해 페이지네이션 정보 출력
            logger.log("API 페이지네이션 정보:", {
                totalRecords: pagination.totalRecords,
                currentPage: pagination.currentPage,
                totalPages: pagination.totalPages,
                nextPage: pagination.nextPage,
                prevPage: pagination.prevPage
            });

            // API 응답 데이터를 화면에 표시할 형식으로 변환
            const displayReviews = reviewInfoList.map((review) => ({
                altKey: review.altKey,
                rating: review.rating.toFixed(1),
                content: review.content,
                date: formatDate(review.createdAt),
                images: review.reviewImageInfoList.map((img) => img.imageUrl)
            }));

            return {
                reviews: displayReviews,
                totalCount: pagination.totalRecords,
                hasMore: pagination.nextPage !== null
            };
        }

        return null;
    } catch (error) {
        logger.error("상품 리뷰 목록 조회 실패:", error);
        return null;
    }
};

/**
 * 날짜 문자열을 "yyyy. MM. dd" 형식으로 포맷팅합니다.
 * @param dateString ISO 날짜 문자열
 * @returns 포맷팅된 날짜 문자열
 */
const formatDate = (dateString: string): string => {
    try {
        // 한국어 로케일 설정
        dayjs.locale("ko");
        return dayjs(dateString).format("YYYY. MM. DD");
    } catch (error) {
        logger.error("날짜 포맷팅 오류:", error);
        return dateString;
    }
};
